import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Paper, Chip, Button, Modal, Fade, TextField, IconButton,
  CircularProgress, Snackbar, alpha, useTheme,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/AddRounded';
import UploadFileIcon from '@mui/icons-material/UploadFileRounded';
import VisibilityIcon from '@mui/icons-material/VisibilityRounded';
import RateReviewIcon from '@mui/icons-material/RateReviewRounded';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import FilterListIcon from '@mui/icons-material/FilterListRounded';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  getAssignmentsList, getAssignmentsByCourseBatch, getAssignmentDetail, createAssignment,
  getAssignmentPdfUrl, getTeacherId, getBranchId,
} from '../api/teacherApi';
import EmptyState from '../components/EmptyState';
import { tokens } from '../theme/tokens';
import { staggerContainer, fadeInUp, hoverLift } from '../utils/motion';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const MotionPaper = motion(Paper);
const MotionBox = motion(Box);

const emptyForm = { title: '', description: '', course: '', batch: '', due_date: '', file: null };

const Assignments = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [courseFilter, setCourseFilter] = useState('');
  const [batchFilter, setBatchFilter] = useState('');

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadAssignments = async (course, batch) => {
    setLoading(true);
    try {
      const res = course || batch
        ? await getAssignmentsByCourseBatch(course, batch, getTeacherId())
        : await getAssignmentsList({ branch_id: getBranchId() });
      const data = res.data;
      setAssignments(Array.isArray(data) ? data : data?.results || []);
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to load assignments.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAssignments(); }, []);

  const handleFilter = () => loadAssignments(courseFilter, batchFilter);
  const handleResetFilter = () => { setCourseFilter(''); setBatchFilter(''); loadAssignments(); };

  const handleViewDetail = async (assignment) => {
    try {
      const res = await getAssignmentDetail(assignment.id);
      setSelectedDetail(res.data);
      setDetailOpen(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpenCreate = () => { setForm(emptyForm); setCreateOpen(true); };

  const handleCreate = async () => {
    if (!form.title || !form.course || !form.batch) {
      setSnackbar({ open: true, message: 'Title, course and batch are required.', severity: 'error' });
      return;
    }
    setSaving(true);
    const formData = new FormData();
    formData.append('title', form.title);
    if (form.description) formData.append('description', form.description);
    formData.append('course', form.course);
    formData.append('batch', form.batch);
    if (form.due_date) formData.append('due_date', form.due_date);
    if (form.file) formData.append('file', form.file);
    const teacherId = getTeacherId();
    if (teacherId) formData.append('teacher', teacherId);
    const branchId = getBranchId();
    if (branchId) formData.append('branch_id', branchId);

    try {
      await createAssignment(formData);
      setSnackbar({ open: true, message: 'Assignment created!', severity: 'success' });
      setCreateOpen(false);
      loadAssignments(courseFilter, batchFilter);
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to create assignment.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <Typography variant="h5">Assignments</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Create assignment
        </Button>
      </Box>

      <MotionPaper
        variants={fadeInUp} initial="hidden" animate="show"
        elevation={0}
        sx={{
          display: 'flex', gap: 2, mb: 2.5, alignItems: 'center', flexWrap: 'wrap',
          p: 2, borderRadius: '16px', border: '1px solid', borderColor: 'divider',
        }}
      >
        <FilterListIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <TextField
          label="Course ID" size="small" value={courseFilter}
          onChange={(e) => setCourseFilter(e.target.value)} sx={{ width: 140 }}
        />
        <TextField
          label="Batch ID" size="small" value={batchFilter}
          onChange={(e) => setBatchFilter(e.target.value)} sx={{ width: 140 }}
        />
        <Button variant="contained" size="small" onClick={handleFilter}>Filter</Button>
        <Button variant="outlined" size="small" onClick={handleResetFilter}>Reset</Button>
      </MotionPaper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : assignments.length > 0 ? (
        <Grid container spacing={2} component={motion.div} variants={staggerContainer} initial="hidden" animate="show">
          {assignments.map((a) => (
            <Grid item xs={12} sm={6} md={4} key={a.id}>
              <MotionPaper
                variants={fadeInUp}
                {...hoverLift}
                elevation={0}
                sx={{
                  p: 2.5, borderRadius: '18px', height: '100%', display: 'flex', flexDirection: 'column',
                  border: '1px solid', borderColor: 'divider',
                  '&:hover': { borderColor: alpha(theme.palette.primary.main, 0.4), boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.14)}` },
                }}
              >
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>{a.title || a.topic}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {a.course_name || `Course ${a.course}`}{(a.batch_name || a.batch) && ` — ${a.batch_name || `Batch ${a.batch}`}`}
                </Typography>
                {a.due_date && (
                  <Chip
                    size="small"
                    label={`Due ${a.due_date}`}
                    sx={{ mb: 2, alignSelf: 'flex-start', backgroundColor: alpha(tokens.amber, 0.16), color: '#a86500' }}
                  />
                )}
                <Box sx={{ mt: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button size="small" startIcon={<VisibilityIcon />} onClick={() => handleViewDetail(a)}>View</Button>
                  <Button
                    size="small" variant="contained" startIcon={<RateReviewIcon />}
                    onClick={() => navigate(`/teacher/assignments/${a.id}/submissions`)}
                  >
                    Submissions
                  </Button>
                </Box>
              </MotionPaper>
            </Grid>
          ))}
        </Grid>
      ) : (
        <EmptyState
          icon={<AssignmentOutlinedIcon />}
          title="No assignments yet"
          subtitle="Create your first assignment and it'll show up here."
        />
      )}

      {/* Detail modal */}
      <Modal open={detailOpen} onClose={() => setDetailOpen(false)} closeAfterTransition>
        <Fade in={detailOpen}>
          <MotionBox
            sx={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              width: { xs: '92vw', sm: 480 }, bgcolor: 'background.paper', borderRadius: '18px',
              boxShadow: 24, p: 3, outline: 'none',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">{selectedDetail?.title}</Typography>
              <IconButton onClick={() => setDetailOpen(false)}><CloseIcon /></IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {selectedDetail?.description}
            </Typography>
            {selectedDetail?.due_date && <Typography variant="body2">Due: {selectedDetail.due_date}</Typography>}
            {selectedDetail?.pdf_file && (
              <Button
                sx={{ mt: 2 }}
                variant="outlined"
                href={getAssignmentPdfUrl(selectedDetail.pdf_file)}
                target="_blank"
                rel="noopener noreferrer"
              >
                View PDF
              </Button>
            )}
          </MotionBox>
        </Fade>
      </Modal>

      {/* Create modal */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} closeAfterTransition>
        <Fade in={createOpen}>
          <MotionBox
            sx={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              width: { xs: '92vw', sm: 440 }, bgcolor: 'background.paper', borderRadius: '18px',
              boxShadow: 24, p: 3, outline: 'none', maxHeight: '85vh', overflowY: 'auto',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Create assignment</Typography>
              <IconButton onClick={() => setCreateOpen(false)}><CloseIcon /></IconButton>
            </Box>
            <TextField
              label="Title" fullWidth sx={{ mb: 2 }}
              value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <TextField
              label="Description (optional)" fullWidth multiline rows={3} sx={{ mb: 2 }}
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label="Course ID" fullWidth
                value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })}
              />
              <TextField
                label="Batch ID" fullWidth
                value={form.batch} onChange={(e) => setForm({ ...form, batch: e.target.value })}
              />
            </Box>
            <TextField
              label="Due date" type="date" fullWidth sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
              value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
            />
            <Button
              component="label" variant="outlined" startIcon={<UploadFileIcon />} fullWidth
              sx={{ mb: 2, py: 1.2, borderStyle: 'dashed', borderRadius: '12px' }}
            >
              {form.file ? form.file.name : 'Attach PDF (optional)'}
              <input type="file" hidden accept="application/pdf" onChange={(e) => setForm({ ...form, file: e.target.files[0] })} />
            </Button>
            <Button variant="contained" fullWidth onClick={handleCreate} disabled={saving} sx={{ py: 1.1 }}>
              {saving ? <CircularProgress size={20} color="inherit" /> : 'Create'}
            </Button>
          </MotionBox>
        </Fade>
      </Modal>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Assignments;
